import axios from "axios";
import {
  isApiConnectionIssueKind,
  reportApiConnectionIssue,
} from "./apiConnectionStatus";
import notify from "./NotificationManager";
import { isBrowser } from "./functions";
import { __ } from "./i18n";

declare const localStorage: any;

export let apiUrl = "";
export let appUrl = "";

export function setApiUrl(url: string) {
  apiUrl = url;
  api.defaults.baseURL = url;
}

export function setAppUrl(url: string) {
  appUrl = url;
}

export const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

api.defaults.withCredentials = true;

if (import.meta.env.VITE_MOCK_API === "1") {
  api.defaults.adapter = async (config) => {
    const { mockApiAdapter } = await import("./mockApi");

    return mockApiAdapter(config);
  };
}

if (import.meta.env.DEV && typeof localStorage !== "undefined") {
  api.defaults.headers["X-Live-Mode"] =
    localStorage.getItem("live-mode") === "true" ? "1" : false;
}

export function shouldRetryOnError(failureCount: number, error: any) {
  if (isBrowser && "onLine" in navigator && !navigator.onLine) {
    return false;
  }

  if (failureCount >= 2) {
    return false;
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status || 0;
    return status < 400 || status >= 500;
  }

  return true;
}

function normalizeApiErrorMessage(value: unknown): string | undefined {
  if (typeof value === "string") {
    const message = value.trim();
    return message || undefined;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeApiErrorMessage).filter(Boolean).join("\n");
  }

  if (!value || typeof value !== "object") {
    return undefined;
  }

  const payload = value as Record<string, unknown>;
  const knownMessage =
    normalizeApiErrorMessage(payload.message) ||
    normalizeApiErrorMessage(payload.title) ||
    normalizeApiErrorMessage(payload.detail) ||
    normalizeApiErrorMessage(payload.error) ||
    normalizeApiErrorMessage(payload.errors);

  if (knownMessage) {
    return knownMessage;
  }

  return Object.values(payload)
    .map(normalizeApiErrorMessage)
    .filter(Boolean)
    .join("\n");
}

export function getApiError(payload: any) {
  if (axios.isAxiosError(payload)) {
    return normalizeApiErrorMessage(payload.response?.data);
  }

  if (typeof payload === "object" && payload.name === "ValidationError") {
    return normalizeApiErrorMessage(payload.message);
  }

  return normalizeApiErrorMessage(payload);
}

function normalizeTraceId(value: unknown) {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (typeof value === "number") {
    return String(value);
  }

  return undefined;
}

function getTraceIdFromObject(payload: any) {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  return (
    normalizeTraceId(payload.trace_id) ||
    normalizeTraceId(payload.traceId) ||
    normalizeTraceId(payload.trace) ||
    normalizeTraceId(payload.request_id) ||
    normalizeTraceId(payload.requestId)
  );
}

function getTraceIdFromHeaders(headers: any) {
  if (!headers) {
    return undefined;
  }

  const headerNames = [
    "x-trace-id",
    "x-request-id",
    "trace-id",
    "trace_id",
    "request-id",
    "request_id",
  ];

  for (const name of headerNames) {
    const value =
      typeof headers.get === "function"
        ? headers.get(name)
        : headers[name] || headers[name.toLowerCase()];

    const traceId = normalizeTraceId(value);
    if (traceId) {
      return traceId;
    }
  }

  return undefined;
}

export function getApiTraceId(payload: any) {
  if (axios.isAxiosError(payload)) {
    return (
      getTraceIdFromObject(payload.response?.data?.error) ||
      getTraceIdFromObject(payload.response?.data) ||
      getTraceIdFromHeaders(payload.response?.headers)
    );
  }

  return getTraceIdFromObject(payload?.error) || getTraceIdFromObject(payload);
}

export type ApiErrorStateKind =
  | "offline"
  | "timeout"
  | "network"
  | "unauthorized"
  | "forbidden"
  | "not-found"
  | "rate-limit"
  | "server"
  | "client"
  | "unknown";

export type ApiErrorState = {
  description: string;
  kind: ApiErrorStateKind;
  title: string;
  tone: "danger" | "warning";
};

function isTimeoutError(error: any) {
  return (
    error?.code === "ECONNABORTED" ||
    error?.code === "ETIMEDOUT" ||
    /timeout/i.test(String(error?.message || ""))
  );
}

export function getApiErrorState(error: any): ApiErrorState {
  if (isBrowser && "onLine" in navigator && !navigator.onLine) {
    return {
      kind: "offline",
      title: __("No internet connection"),
      description: __(
        "Your device is offline. Check your connection and try again.",
      ),
      tone: "warning",
    };
  }

  if (axios.isAxiosError(error)) {
    if (isTimeoutError(error)) {
      return {
        kind: "timeout",
        title: __("Request timed out"),
        description: __(
          "The server took too long to respond. Check your connection or try again in a moment.",
        ),
        tone: "warning",
      };
    }

    if (!error.response) {
      return {
        kind: "network",
        title: __("Could not connect to the server"),
        description: __(
          "The request did not reach the server or the browser blocked the response. Check your connection and try again.",
        ),
        tone: "warning",
      };
    }

    const status = error.response.status || 0;

    if (status === 401) {
      return {
        kind: "unauthorized",
        title: __("Session expired"),
        description: __("Sign in again to continue using the panel."),
        tone: "warning",
      };
    }

    if (status === 403) {
      return {
        kind: "forbidden",
        title: __("You do not have access to this section"),
        description: __(
          "Your account does not have permission to view this page. Ask the account owner to update your access if needed.",
        ),
        tone: "warning",
      };
    }

    if (status === 404) {
      return {
        kind: "not-found",
        title: __("Data not found"),
        description: __("The requested information could not be found."),
        tone: "warning",
      };
    }

    if (status === 429) {
      return {
        kind: "rate-limit",
        title: __("Too many requests"),
        description: __(
          "Too many requests were sent. Please wait a moment and try again.",
        ),
        tone: "warning",
      };
    }

    if (status >= 500) {
      return {
        kind: "server",
        title: __("Server error"),
        description: __(
          "The server encountered a problem. Please try again in a moment.",
        ),
        tone: "danger",
      };
    }

    const message = getApiError(error);
    return {
      kind: "client",
      title: __("Request could not be completed"),
      description:
        message ||
        __("The request could not be completed. Check the information and try again."),
      tone: "warning",
    };
  }

  return {
    kind: "unknown",
    title: __("Something went wrong."),
    description:
      error instanceof Error
        ? error.message
        : __(
            "The page could not be loaded. Refresh the page or try again in a moment.",
          ),
    tone: "danger",
  };
}

function getApiErrorFallback(error: any) {
  if (isBrowser && "onLine" in navigator && !navigator.onLine) {
    return __("You are offline. Check your connection and try again.");
  }

  if (axios.isAxiosError(error)) {
    return __("Oops! An error occurred. Please try again later!");
  }

  return error instanceof Error
    ? error.message
    : __("Oops! An error occurred. Please try again later!");
}

export function handleApiError(
  error: any,
  hideError: boolean,
  options: { showFallback?: boolean } = {},
) {
  const errorState = getApiErrorState(error);

  if (isApiConnectionIssueKind(errorState.kind)) {
    reportApiConnectionIssue(errorState);
  }

  const message =
    getApiError(error) ||
    (options.showFallback ? getApiErrorFallback(error) : undefined);
  const traceId = getApiTraceId(error);
  if (message) {
    if (!hideError) {
      notify.error(
        traceId
          ? {
              title: message,
              message: `${__("Trace ID")}: ${traceId}`,
              timeout: 6000,
            }
          : message,
      );
    }
    if (isBrowser)
      console.error(
        "Error message: " + message + (traceId ? " \ntrace id:" + traceId : ""),
      );
  }
}
